/**
 * PhaseAccumulator — Collapsible dropdown for building up material costs
 * or hours from individual items. Supports multi-tap quantity increments.
 *
 * Tap item → qty +1. Quantity badge shows ×N. Inline clear per item.
 * Shows max 3 summary pills when collapsed. Global reset at bottom.
 */

import { useState, useRef } from 'react';
import { T } from '@/styles/tokens';

interface PhaseAccumulatorProps {
  items: Array<{ label: string; value: number }>;
  quantities: Map<number, number>;       // idx → qty (0 or absent = unused)
  onIncrement: (idx: number, unitValue: number) => void;
  onDecrement: (idx: number, unitValue: number) => void;
  onClearItem: (idx: number, totalValue: number) => void;
  onReset: () => void;
  unit: '$' | 'hrs';
}

function formatValue(value: number, unit: '$' | 'hrs'): string {
  return unit === '$' ? `$${value}` : `${value}h`;
}

export function PhaseAccumulator({
  items, quantities, onIncrement, onDecrement, onClearItem, onReset, unit,
}: PhaseAccumulatorProps) {
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  if (items.length === 0) return null;

  // Count items with qty > 0
  const activeEntries = [...quantities.entries()].filter(([, qty]) => qty > 0);
  const activeCount = activeEntries.length;
  const recentEntries = activeEntries.slice(-3);

  return (
    <div style={{ marginTop: 12, position: 'relative' }} ref={containerRef}>
      {/* Summary pills — max 3 most recent */}
      {activeCount > 0 && !expanded && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {recentEntries.map(([idx, qty]) => {
            const item = items[idx];
            if (!item) return null;
            return (
              <span
                key={idx}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: T.tealSoft,
                  border: `1px solid ${T.primary}22`,
                  fontSize: 12,
                  fontWeight: 500,
                  color: T.primaryDark,
                  fontFamily: T.fontFamily,
                }}
              >
                {item.label}
                {qty > 1 && <span style={{ opacity: 0.7 }}>&times;{qty}</span>}
                <span style={{ fontWeight: 600 }}>{formatValue(item.value * qty, unit)}</span>
              </span>
            );
          })}
          {activeCount > 3 && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 8px',
              fontSize: 11,
              fontWeight: 600,
              color: T.charcoal400,
              fontFamily: T.fontFamily,
            }}>
              +{activeCount - 3} more
            </span>
          )}
        </div>
      )}

      {/* Toggle row */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '8px 12px',
          borderRadius: 10,
          border: `1px solid ${expanded ? T.primary + '33' : T.border}`,
          background: expanded ? T.tealGlass : T.cream,
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          fontFamily: T.fontFamily,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 500, color: T.charcoal600 }}>
          {activeCount > 0 ? `${activeCount} of ${items.length} added` : 'Add by item'}
        </span>
        <svg
          width="14" height="14" viewBox="0 0 16 16" fill="none"
          style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.15s ease' }}
        >
          <path d="M4 6l4 4 4-4" stroke={T.charcoal400} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Overlay checklist */}
      {expanded && (
        <>
          {/* Backdrop to close */}
          <div
            onClick={() => setExpanded(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 49,
              background: 'transparent',
            }}
          />
          <div style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '100%',
            marginTop: 4,
            zIndex: 50,
            padding: '10px 12px',
            borderRadius: 12,
            background: T.white,
            border: `1px solid ${T.border}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)',
            maxHeight: 260,
            overflowY: 'auto',
          }}>
            {items.map((item, idx) => {
              const qty = quantities.get(idx) || 0;
              const active = qty > 0;
              return (
                <div
                  key={item.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '9px 0',
                    borderBottom: idx < items.length - 1 ? `1px solid ${T.border}` : 'none',
                  }}
                >
                  {/* Tap area: label + add action */}
                  <button
                    onClick={() => onIncrement(idx, item.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flex: 1,
                      minWidth: 0,
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontFamily: T.fontFamily,
                      padding: 0,
                    }}
                  >
                    {/* Quantity badge or empty circle */}
                    <div style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      border: active ? 'none' : `1.5px solid ${T.charcoal200}`,
                      background: active ? T.primary : T.white,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.12s ease',
                    }}>
                      {active && (
                        <span style={{
                          fontSize: 11, fontWeight: 700, color: T.white,
                          fontFamily: T.fontFamily, lineHeight: 1,
                        }}>
                          {qty}
                        </span>
                      )}
                    </div>
                    <span style={{
                      fontSize: 13, fontWeight: 500, color: T.text,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {item.label}
                    </span>
                  </button>

                  {/* Right side: price + inline clear */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <span style={{
                      fontSize: 13, fontWeight: 600,
                      color: active ? T.primary : T.charcoal400,
                      fontVariantNumeric: 'tabular-nums',
                      fontFamily: T.fontFamily,
                    }}>
                      {qty > 1 && (
                        <span style={{ fontWeight: 400, opacity: 0.7, marginRight: 2 }}>&times;{qty} </span>
                      )}
                      {formatValue(item.value * Math.max(qty, 1), unit)}
                    </span>

                    {/* Inline clear button — only when active */}
                    {active && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onClearItem(idx, item.value * qty); }}
                        style={{
                          width: 20, height: 20, borderRadius: 10,
                          border: 'none',
                          background: T.charcoal200 + '44',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', padding: 0, flexShrink: 0,
                        }}
                      >
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M3 3l6 6M9 3l-6 6" stroke={T.charcoal400} strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Reset all */}
            {activeCount > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); onReset(); setExpanded(false); }}
                style={{
                  marginTop: 8,
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  fontSize: 12,
                  fontWeight: 500,
                  color: T.charcoal300,
                  cursor: 'pointer',
                  fontFamily: T.fontFamily,
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                }}
              >
                Reset all
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
